import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  Select,
  Option,
  Stack,
  Typography,
  Alert,
  Divider,
} from '@mui/joy';
import { scheduleService } from '@/services/scheduleService';
import { shiftTypeService } from '@/services/shiftTypeService';
import { usePermissions } from '@/utils/permissions';
import { ScheduleWithDetails, CreateScheduleRequest, UpdateScheduleRequest } from '@/types/schedule';
import { ShiftType } from '@/types/shiftType';
import { User } from '@/types/user';
import toast from 'react-hot-toast';

interface ScheduleFormData {
  employee_id: number | null;
  shift_type_id: number | null;
  start_datetime: string;
  end_datetime: string;
  status: 'draft' | 'published' | 'updated';
}

interface ScheduleFormErrors {
  employee_id?: string;
  shift_type_id?: string;
  start_datetime?: string;
  end_datetime?: string;
  general?: string;
}

interface ScheduleFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingSchedule?: ScheduleWithDetails | null;
}

export default function ScheduleFormModal({
  open,
  onClose,
  onSuccess,
  editingSchedule = null,
}: ScheduleFormModalProps) {
  const { canPerform } = usePermissions();
  const canEdit = canPerform('schedules', 'edit');

  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
  const [qualifiedEmployees, setQualifiedEmployees] = useState<User[]>([]);
  const [formData, setFormData] = useState<ScheduleFormData>({
    employee_id: null,
    shift_type_id: null,
    start_datetime: '',
    end_datetime: '',
    status: 'draft',
  });
  const [errors, setErrors] = useState<ScheduleFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch shift types when modal opens
  useEffect(() => {
    if (open) {
      const fetchShiftTypes = async () => {
        try {
          setIsLoading(true);
          const shiftTypesData = await shiftTypeService.getAllShiftTypes();
          setShiftTypes(shiftTypesData);
        } catch (error) {
          console.error('Failed to fetch shift types:', error);
          toast.error('Failed to load shift types');
        } finally {
          setIsLoading(false);
        }
      };
      fetchShiftTypes();
    }
  }, [open]);

  // Initialize form when editing or modal opens
  useEffect(() => {
    if (open) {
      if (editingSchedule) {
        setFormData({
          employee_id: editingSchedule.employee_id,
          shift_type_id: editingSchedule.shift_type_id,
          start_datetime: new Date(editingSchedule.start_datetime).toISOString().slice(0, 16),
          end_datetime: new Date(editingSchedule.end_datetime).toISOString().slice(0, 16),
          status: editingSchedule.status,
        });
        fetchQualifiedEmployees(editingSchedule.shift_type_id);
      } else {
        setFormData({
          employee_id: null,
          shift_type_id: null,
          start_datetime: '',
          end_datetime: '',
          status: 'draft',
        });
        setQualifiedEmployees([]);
      }
      setErrors({});
    }
  }, [open, editingSchedule]);

  // Helper function to set default times based on shift type
  const setDefaultTimesForShiftType = (shiftTypeId: number | null) => {
    if (!shiftTypeId) {
      setFormData(prev => ({
        ...prev,
        start_datetime: '',
        end_datetime: ''
      }));
      return;
    }

    const selectedShiftType = shiftTypes.find(st => st.id === shiftTypeId);
    if (selectedShiftType && selectedShiftType.default_start_time && selectedShiftType.default_end_time) {
      const today = new Date();
      const dateString = today.toISOString().split('T')[0];
      const startDateTime = `${dateString}T${selectedShiftType.default_start_time}`;
      const endDateTime = `${dateString}T${selectedShiftType.default_end_time}`;
      
      setFormData(prev => ({
        ...prev,
        start_datetime: startDateTime,
        end_datetime: endDateTime
      }));
    }
  };

  // Helper function to adjust end date when start date changes
  const adjustEndDateForStartDateChange = (newStartDateTime: string) => {
    if (!formData.shift_type_id || !newStartDateTime) {
      return;
    }

    const selectedShiftType = shiftTypes.find(st => st.id === formData.shift_type_id);
    if (!selectedShiftType || !selectedShiftType.default_start_time || !selectedShiftType.default_end_time) {
      return;
    }

    try {
      const newStartDate = new Date(newStartDateTime);
      const defaultStartTime = new Date(`2000-01-01T${selectedShiftType.default_start_time}`);
      const defaultEndTime = new Date(`2000-01-01T${selectedShiftType.default_end_time}`);
      const timeDifference = defaultEndTime.getTime() - defaultStartTime.getTime();
      const newEndDate = new Date(newStartDate.getTime() + timeDifference);
      const newEndDateTime = newEndDate.toISOString().slice(0, 16);
      
      setFormData(prev => ({
        ...prev,
        end_datetime: newEndDateTime
      }));
    } catch (error) {
      console.error('Error adjusting end date:', error);
    }
  };

  // Helper function to fetch qualified employees for a shift type
  const fetchQualifiedEmployees = async (shiftTypeId: number | null) => {
    if (!shiftTypeId) {
      setQualifiedEmployees([]);
      return;
    }

    try {
      const qualifiedEmployeesData = await shiftTypeService.getEmployeesForShiftType(shiftTypeId);
      setQualifiedEmployees(qualifiedEmployeesData);
    } catch (error) {
      console.error('Failed to fetch qualified employees:', error);
      toast.error('Failed to load qualified employees');
      setQualifiedEmployees([]);
    }
  };

  // Handle shift type selection
  const handleShiftTypeChange = async (shiftTypeId: number | null) => {
    setFormData(prev => ({
      ...prev,
      shift_type_id: shiftTypeId,
      employee_id: null // Reset employee when shift type changes
    }));
    
    setDefaultTimesForShiftType(shiftTypeId);
    await fetchQualifiedEmployees(shiftTypeId);
  };

  // Handle start date/time change
  const handleStartDateTimeChange = (newStartDateTime: string) => {
    setFormData(prev => ({
      ...prev,
      start_datetime: newStartDateTime
    }));
    
    adjustEndDateForStartDateChange(newStartDateTime);
  };

  const validateForm = (): boolean => {
    const newErrors: ScheduleFormErrors = {};

    if (!formData.employee_id || formData.employee_id === 0) {
      newErrors.employee_id = 'Employee is required';
    }
    if (!formData.shift_type_id || formData.shift_type_id === 0) {
      newErrors.shift_type_id = 'Shift type is required';
    }
    if (!formData.start_datetime) {
      newErrors.start_datetime = 'Start time is required';
    }
    if (!formData.end_datetime) {
      newErrors.end_datetime = 'End time is required';
    }

    if (formData.start_datetime && formData.end_datetime) {
      const start = new Date(formData.start_datetime);
      const end = new Date(formData.end_datetime);
      if (start >= end) {
        newErrors.end_datetime = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      if (editingSchedule) {
        // Update existing schedule
        const updateData: UpdateScheduleRequest = {
          employee_id: formData.employee_id!,
          shift_type_id: formData.shift_type_id!,
          start_datetime: new Date(formData.start_datetime).toISOString(),
          end_datetime: new Date(formData.end_datetime).toISOString(),
          status: formData.status,
        };
        
        await scheduleService.updateSchedule(editingSchedule.id, updateData);
        toast.success('Schedule updated successfully');
      } else {
        // Create new schedule
        const createData: CreateScheduleRequest = {
          employee_id: formData.employee_id!,
          shift_type_id: formData.shift_type_id!,
          start_datetime: new Date(formData.start_datetime).toISOString(),
          end_datetime: new Date(formData.end_datetime).toISOString(),
          status: formData.status,
        };
        
        await scheduleService.createSchedule(createData);
        toast.success('Schedule created successfully');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save schedule:', error);
      toast.error(`Failed to ${editingSchedule ? 'update' : 'create'} schedule`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      employee_id: null,
      shift_type_id: null,
      start_datetime: '',
      end_datetime: '',
      status: 'draft',
    });
    setQualifiedEmployees([]);
    setErrors({});
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog size="lg">
        <ModalClose />
        <Typography level="h4" mb={2}>
          {editingSchedule ? (canEdit ? 'Edit Schedule' : 'View Schedule') : 'Create Schedule'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {errors.general && (
              <Alert color="danger" variant="soft">
                {errors.general}
              </Alert>
            )}

            {editingSchedule && !canEdit && (
              <Alert color="neutral" variant="soft">
                You don't have permission to edit schedules.
              </Alert>
            )}

            <FormControl error={!!errors.shift_type_id}>
              <Typography level="body-sm" mb={1}>
                Shift Type *
              </Typography>
              <Select
                placeholder="Select shift type"
                value={formData.shift_type_id ? String(formData.shift_type_id) : ''}
                onChange={(_, value) => handleShiftTypeChange(value ? Number(value) : null)}
                disabled={isLoading || !canEdit}
              >
                {shiftTypes.map((shiftType) => (
                  <Option key={shiftType.id} value={String(shiftType.id)}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: shiftType.color,
                        }}
                      />
                      {shiftType.name}
                    </Stack>
                  </Option>
                ))}
              </Select>
              {errors.shift_type_id && (
                <Typography level="body-xs" color="danger" mt={1}>
                  {errors.shift_type_id}
                </Typography>
              )}
            </FormControl>

            <FormControl error={!!errors.employee_id}>
              <Typography level="body-sm" mb={1}>
                Employee *
              </Typography>
              <Select
                placeholder={formData.shift_type_id ? "Select employee" : "Select shift type first"}
                value={formData.employee_id ? String(formData.employee_id) : ''}
                onChange={(_, value) => setFormData({ ...formData, employee_id: value ? Number(value) : null })}
                disabled={!formData.shift_type_id || !canEdit}
              >
                {qualifiedEmployees.map((employee) => (
                  <Option key={employee.id} value={String(employee.id)}>
                    {employee.first_name} {employee.last_name}
                  </Option>
                ))}
              </Select>
              {errors.employee_id && (
                <Typography level="body-xs" color="danger" mt={1}>
                  {errors.employee_id}
                </Typography>
              )}
              {formData.shift_type_id && qualifiedEmployees.length === 0 && (
                <Typography level="body-xs" color="warning" mt={1}>
                  No employees are qualified for this shift type
                </Typography>
              )}
            </FormControl>

            <Grid container spacing={2}>
              <Grid xs={6}>
                <FormControl error={!!errors.start_datetime}>
                  <Typography level="body-sm" mb={1}>
                    Start Time *
                  </Typography>
                  <Input
                    type="datetime-local"
                    value={formData.start_datetime}
                    onChange={(e) => handleStartDateTimeChange(e.target.value)}
                    error={!!errors.start_datetime}
                    disabled={!canEdit}
                    size="lg"
                  />
                  {errors.start_datetime && (
                    <Typography level="body-xs" color="danger" mt={1}>
                      {errors.start_datetime}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={6}>
                <FormControl error={!!errors.end_datetime}>
                  <Typography level="body-sm" mb={1}>
                    End Time *
                  </Typography>
                  <Input
                    type="datetime-local"
                    value={formData.end_datetime}
                    onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                    error={!!errors.end_datetime}
                    disabled={!canEdit}
                    size="lg"
                  />
                  {errors.end_datetime && (
                    <Typography level="body-xs" color="danger" mt={1}>
                      {errors.end_datetime}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <FormControl>
              <Typography level="body-sm" mb={1}>
                Status
              </Typography>
              <Select
                value={formData.status}
                onChange={(_, value) => setFormData({ ...formData, status: value || 'draft' })}
                disabled={!canEdit}
              >
                <Option value="draft">Draft</Option>
                <Option value="published">Published</Option>
              </Select>
            </FormControl>

            <Divider />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleClose}>
                {editingSchedule && !canEdit ? 'Close' : 'Cancel'}
              </Button>
              {canEdit && (
                <Button type="submit" loading={isSubmitting}>
                  {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
                </Button>
              )}
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

